import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Removes character-fee cart lines whose parent product is no longer in the cart.
 */
let cleaning = false;

/**
 * @param {object} item
 * @returns {Record<string, string>}
 */
function getLineProperties(item) {
  const props = item?.properties;
  /** @type {Record<string, string>} */
  const map = {};
  if (!props) return map;

  if (Array.isArray(props)) {
    for (const entry of props) {
      if (Array.isArray(entry) && entry.length >= 2) {
        map[String(entry[0])] = String(entry[1] ?? '');
      } else if (entry && typeof entry === 'object' && 'name' in entry) {
        map[String(entry.name)] = String(entry.value ?? '');
      }
    }
    return map;
  }

  for (const [key, value] of Object.entries(props)) {
    map[key] = String(value ?? '');
  }
  return map;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function numericId(value) {
  const match = String(value || '').match(/(\d+)/);
  return match ? match[1] : '';
}

/**
 * @param {object} item
 * @returns {boolean}
 */
function isCharacterFeeItem(item) {
  const flag = (getLineProperties(item)._character_fee || '').toLowerCase();
  return flag === '1' || flag === 'true';
}

/**
 * @param {object} fee
 * @param {object[]} nonFeeItems
 * @returns {boolean}
 */
function feeHasParentInCart(fee, nonFeeItems) {
  const props = getLineProperties(fee);
  const parentId = numericId(props._parent_product_id);
  const parentTitle = (
    props._parent_product_title ||
    props['_For product'] ||
    props['_עבור מוצר'] ||
    ''
  ).trim();

  return nonFeeItems.some((item) => {
    const itemIds = [item.product_id, item.id, item.variant_id].map(numericId).filter(Boolean);
    if (parentId && itemIds.includes(parentId)) return true;

    const itemTitle = String(item.product_title || item.title || '').trim();
    if (parentTitle && itemTitle && itemTitle === parentTitle) return true;

    return false;
  });
}

/**
 * @param {object} cart
 * @returns {object[]}
 */
function findOrphanFees(cart) {
  const items = cart?.items || [];
  const nonFee = items.filter((item) => !isCharacterFeeItem(item));
  const fees = items.filter(isCharacterFeeItem);

  if (!fees.length) return [];

  // Only fees left in the cart — they cannot stay on their own.
  if (!nonFee.length) return fees;

  return fees.filter((fee) => {
    const props = getLineProperties(fee);
    const hasParentHint = Boolean(
      numericId(props._parent_product_id) || props._parent_product_title || props['_For product'] || props['_עבור מוצר']
    );

    // If we cannot identify a parent, do not delete while other products exist.
    if (!hasParentHint) return false;

    return !feeHasParentInCart(fee, nonFee);
  });
}

async function removeOrphanCharacterFees() {
  if (cleaning) return;
  cleaning = true;

  try {
    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    if (!cart || !Array.isArray(cart.items)) return;

    const orphans = findOrphanFees(cart);
    if (!orphans.length) return;

    for (const orphan of orphans) {
      const body = JSON.stringify({
        id: String(orphan.key),
        quantity: 0,
      });
      await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    }

    document.dispatchEvent(
      new CartUpdateEvent({}, 'character-fee-cart', {
        source: 'character-fee-cart',
      })
    );

    if (typeof window.refreshCartDrawer === 'function') {
      window.refreshCartDrawer();
    }
  } catch (error) {
    console.error('[character-fee-cart] Failed to remove orphan fee lines:', error);
  } finally {
    cleaning = false;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {{ data?: { source?: string } }} */ (event).detail;
  if (detail?.data?.source === 'character-fee-cart') return;
  if (detail?.data?.source === 'character-priced-text') return;
  removeOrphanCharacterFees();
});
